import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../context/StoreContext";

export default function Navbar() {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { cart, user, userRole } = useStore() || {};

  const navItems = [
    { id: 1, label: "Inicio", href: "/", color: "neonBlue" },
    { id: 2, label: "Nosotros", href: "/about", color: "neonPurple" },
    { id: 3, label: "Servicios", href: "/services", color: "neonRed" },
    { id: 4, label: "Cursos", href: "/courses", color: "neonBlue" },
    { id: 5, label: "Software", href: "/software", color: "neonPurple" },
    { id: 6, label: "Contacto", href: "/contact", color: "neonRed" },
    ...(userRole === "admin"
      ? [
          { id: 7, label: "Admin", href: "/admin", color: "neonRed" },
          { id: 8, label: "Cuenta", href: "/account", color: "neonPurple" }
        ]
      : [{ id: 7, label: user ? "Cuenta" : "Login", href: user ? "/account" : "/login", color: "neonPurple" }]
    ),
  ];

  return (
    <nav aria-label="Navegación principal" className="fixed w-full backdrop-blur-xl bg-bgDark/80 border-b-2 border-neonBlue/40 px-3 md:px-4 py-2 md:py-3 z-50 shadow-[0_0_25px_rgba(0,245,255,0.25)] overflow-hidden">
      {/* Neon accents */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-10 left-10 w-40 h-40 bg-neonBlue rounded-full blur-3xl opacity-10" />
        <div className="absolute -top-12 right-12 w-44 h-44 bg-neonRed rounded-full blur-3xl opacity-10" />
        <motion.div
          className="absolute bottom-0 left-0 w-full h-[2px] bg-linear-to-r from-neonBlue via-neonPurple to-neonRed"
          animate={{ opacity: [0.35, 0.9, 0.35] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute inset-x-6 bottom-0 h-px bg-linear-to-r from-neonBlue/60 via-neonPurple/60 to-neonRed/60 rounded-full"
          animate={{ opacity: [0.3, 0.9, 0.3] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto flex justify-between items-center px-2 md:px-6">
        {/* Logo */}
        <Link to="/">
          <motion.div
            className="text-xl md:text-2xl font-black cursor-pointer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="relative inline-flex items-center gap-1">
              <motion.span
                className="text-neonPurple neon-purple"
                animate={{
                  textShadow: [
                    "0 0 10px #8a2be2, 0 0 22px #8a2be2",
                    "0 0 18px #8a2be2, 0 0 40px #8a2be2",
                    "0 0 10px #8a2be2, 0 0 22px #8a2be2",
                  ],
                }}
                transition={{ duration: 2.8, repeat: Infinity }}
              >
                codigo
              </motion.span>
              <motion.span
                className="text-neonRed neon-red"
                animate={{
                  textShadow: [
                    "0 0 10px #ff073a, 0 0 22px #ff073a",
                    "0 0 18px #ff073a, 0 0 40px #ff073a",
                    "0 0 10px #ff073a, 0 0 22px #ff073a",
                  ],
                }}
                transition={{ duration: 2.8, repeat: Infinity, delay: 0.2 }}
              >
                29
              </motion.span>
              <span className="absolute -inset-2 rounded-xl border border-white/5 bg-white/5/5 blur-[1px] pointer-events-none" />
            </div>
          </motion.div>
        </Link>

        {/* Nav Items */}
        <ul className="hidden lg:flex gap-4 xl:gap-8 items-center">
          {navItems.map((item) => (
            <Link key={item.id} to={item.href}>
              <motion.li
                className="relative cursor-pointer uppercase font-bold tracking-widest text-sm"
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
                whileHover={{ y: -2 }}
              >
                <span
                  className={`transition-all duration-300 ${
                    hoveredItem === item.id
                      ? "text-neonRed drop-shadow-[0_0_14px_rgba(255,7,58,0.9)]"
                      : "text-textSoft hover:text-white"
                  }`}
                >
                  {item.label}
                </span>

                {/* Animated Underline */}
                {hoveredItem === item.id && (
                  <motion.div
                    className="absolute bottom-0 left-0 h-1 rounded-full bg-neonRed shadow-neon-red"
                    layoutId="underline"
                    animate={{ width: "100%" }}
                    initial={{ width: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.li>
            </Link>
          ))}
        </ul>

        {/* Cart + Mobile Toggle */}
        <div className="flex items-center gap-2 md:gap-3">


          {/* Mobile menu toggle */}
          <button
            className="lg:hidden relative w-9 h-9 rounded-lg border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Abrir menú"
          >
            <span className="absolute inset-0 bg-linear-to-tr from-neonBlue/10 via-transparent to-neonRed/10" />
            <motion.span
              className="relative flex flex-col gap-1.5"
              animate={menuOpen ? { rotate: 90 } : { rotate: 0 }}
              transition={{ duration: 0.2 }}
            >
              <span
                className={`h-0.5 w-5 rounded-full bg-white transition-all ${
                  menuOpen ? "translate-y-2 rotate-45" : ""
                }`}
              />
              <span
                className={`h-0.5 w-4 rounded-full bg-white transition-all ${
                  menuOpen ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`h-0.5 w-5 rounded-full bg-white transition-all ${
                  menuOpen ? "-translate-y-2 -rotate-45" : ""
                }`}
              />
            </motion.span>
          </button>
        </div>
      </div>

      {/* Mobile / Tablet Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t border-white/10 bg-bgDark/95 backdrop-blur-xl"
          >
            <div className="max-w-7xl mx-auto px-4 pt-2 pb-4 space-y-3">
              <ul className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={item.href}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-white/5 transition-colors"
                    >
                      <span className="text-sm font-semibold tracking-wide text-textSoft">
                        {item.label}
                      </span>
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          item.color === "neonBlue"
                            ? "bg-neonBlue shadow-neon-blue"
                            : item.color === "neonRed"
                            ? "bg-neonRed shadow-neon-red"
                            : "bg-neonPurple shadow-neon-purple"
                        }`}
                      />
                    </Link>
                  </li>
                ))}
              </ul>

            
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}